import { RSAA } from 'redux-api-middleware';
import { Actions } from '../actionTypes/actionTypes';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { Selectors } from '../common/rootSelectors';
import { getSalesCheckinBffEndPoint } from '@makemydeal/dr-sales-checkin-ui-utils';
import type { GetState } from '../common/types/SalesCheckinRootState';
import { getActiveDeal, getIsAmdOriginated } from '../activeDeal/selector';
import { getDealPdfVersion } from '../dealPdf/selector';
import { getDealerInfoForPdf } from '../dealership/selector';
import { getPdfShareLinkCache, getPdfShareLinkRequestStatus } from './selector';
import { setCustomerCardStateAction, handleGlobalErrorAction } from '../common/commonActions';
import { CustomerCardState, ConversationTypeEnum, DeepLinks } from '@makemydeal/dr-sales-checkin-types';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

const PDF_SHARE_LINK_FAILURE_MESSAGE = 'Something went wrong creating the Deal PDF share link. Please try again.';

const openDealPdfInbox = () =>
    setCustomerCardStateAction(CustomerCardState.INBOX, {
        defaultTab: 'inbox',
        occDefaultTab: ConversationTypeEnum.TEXT,
        message: DeepLinks.DealPdf
    });

/**
 * Mints a shortened, durable share link for the PDF currently on Deal Summary preview,
 * then opens the customer inbox with it. Reuses the link from a previous click as long
 * as it was minted for the dealPdfVersion currently on display.
 */
export const getPdfShareLink = (selectors: Selectors) => async (dispatch: any, getState: GetState) => {
    const state = getState();
    const dealXgId = getActiveDeal(state).dealId;
    const dealXgVersion = getDealPdfVersion(state);

    if (!dealXgId || !dealXgVersion) {
        return;
    }

    const { shortUrl, cachedForVersion } = getPdfShareLinkCache(state);
    if (shortUrl && cachedForVersion === dealXgVersion) {
        dispatch(openDealPdfInbox());
        return;
    }

    // A click that lands while a previous click's fetch is still in flight would otherwise
    // pass the cache check above too (nothing's cached yet) and fire a second, redundant
    // S3 + Short.io round trip. The in-flight request already opens the inbox once it
    // resolves, so this click doesn't need to do anything.
    if (getPdfShareLinkRequestStatus(state) === ApiRequestStatus.Loading) {
        return;
    }

    const environment = selectors.getEnvironment(state);
    const endpoint = getSalesCheckinBffEndPoint(`deals/${dealXgId}/versions/${dealXgVersion}/pdf-share-link`, environment);
    const meta = { dealXgVersion };
    const isAmdOriginated = getIsAmdOriginated(state);
    const body = isAmdOriginated ? { dealData: getActiveDeal(state), dealerInfo: getDealerInfoForPdf(state) } : {};
    const request = {
        endpoint,
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        types: [
            Actions.FETCH_PDF_SHARE_LINK_REQUEST,
            { type: Actions.FETCH_PDF_SHARE_LINK_SUCCESS, meta },
            Actions.FETCH_PDF_SHARE_LINK_FAILURE
        ],
        fetch: async (...args) => await fetch(...args)
    } satisfies RSAACallRequest;

    // Several middlewares in this store's chain call `next(action)` without `return`-ing
    // its result, so the RSAA promise's resolved value can't be trusted here. The reducer
    // update it triggers is still synchronous and has already landed by the time this
    // `await` settles, so re-read state instead of relying on the dispatch's return value.
    await dispatch({ [RSAA]: request });

    const { shortUrl: freshShortUrl } = getPdfShareLinkCache(getState());
    if (freshShortUrl) {
        dispatch(openDealPdfInbox());
    } else {
        dispatch(handleGlobalErrorAction(PDF_SHARE_LINK_FAILURE_MESSAGE));
    }
};
